// The Vendor PO list's Export: pick ONE purchase order and download it as the
// file its supplier gets (PDF or Excel), not a CSV of the list. Preet,
// 2026-10-20: "it should ask me what vendor po i want to export in a proper
// dropdown ... usable with a search bar", and it has to stay on screen at any width.
//
// The same shape as `ConfirmedOrderSearchSelect`: opens with the most recent
// POs, the server does the searching (`listVendorPurchaseOrders` — PO number,
// vendor, RFQ, quotation, vessel, client) and pages it, a last-one-wins guard
// so a slow reply cannot repopulate a list you have moved past, keyboard and
// outside-click. The panel is fixed and portalled like `ModuleInfoButton`, so
// no scrolling header can clip it, and it is clamped to the viewport.
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2 } from 'lucide-react';
import {
  downloadVendorPurchaseOrder,
  fetchVendorPurchaseOrders,
  vendorPoSendErrorMessage,
  type VendorPurchaseOrderDto,
} from '../../lib/vendorPurchaseOrderApi';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import Button from '../../components/ui/Button';
import SegmentedControl from '../../components/ui/SegmentedControl';
import StatusBadge from '../../components/StatusBadge';
import { formatCurrency } from '../../utils/priceUtils';
import { formatDate as fmtDate } from '../../utils/dateFormat';

const RESULT_LIMIT = 20;
const GAP = 4;
const EDGE = 12;
const PANEL_WIDTH = 420;
// Below this the panel would be too short to show a search box and one row.
const MIN_HEIGHT = 220;

type Format = 'excel' | 'pdf';

interface VendorPoExportPickerProps {
  /** The list as a CSV, kept as the panel's last line. */
  onExportList: () => void;
  /** The list has no rows to put in that CSV. */
  listEmpty: boolean;
}

export const VendorPoExportPicker: React.FC<VendorPoExportPickerProps> = ({ onExportList, listEmpty }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const [format, setFormat] = useState<Format>('excel');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VendorPurchaseOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const beginRequest = useLatestRequest();

  // Right-aligned under the button (it sits at the right end of the header),
  // never wider than the screen, pulled up so it cannot hang off the bottom.
  // ponytail: no flip above the button — it lives in the top header band.
  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(PANEL_WIDTH, vw - EDGE * 2);
    const left = Math.min(Math.max(EDGE, r.right - width), vw - EDGE - width);
    const top = Math.max(EDGE, Math.min(r.bottom + GAP, vh - EDGE - MIN_HEIGHT));
    setPos({ top, left, width, maxHeight: vh - top - EDGE });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    // A fixed panel does not travel with a scrolling ancestor; capture catches any container.
    const onReflow = () => place();
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside, { passive: true });
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, place]);

  // An empty query is a real search: the most recent POs, so the list is browsable before you type.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const isCurrent = beginRequest();
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetchVendorPurchaseOrders(1, RESULT_LIMIT, query.trim());
        if (cancelled || !isCurrent()) return;
        setResults(res.vendorPurchaseOrders || []);
        setFailed(false);
        setHighlight(0);
      } catch {
        // "Could not load" must read differently from "there are none" (invariant 6: no server prose).
        if (!cancelled && isCurrent()) {
          setResults([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled && isCurrent()) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, beginRequest]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setDownloadError(null);
    triggerRef.current?.focus();
  };

  const choose = async (po: VendorPurchaseOrderDto) => {
    if (downloadingId) return;
    setDownloadingId(po._id);
    setDownloadError(null);
    try {
      await downloadVendorPurchaseOrder(po, format);
      close();
    } catch (err: unknown) {
      // By code: a PO still waiting on a case quantity says which (the preview's one refusal).
      setDownloadError(vendorPoSendErrorMessage(err, `${po.poNumber || 'This purchase order'} could not be exported. Try again.`));
    } finally {
      setDownloadingId(null);
    }
  };

  // Keep the keyboard's row in sight: the list scrolls inside a panel that may hold two rows.
  useEffect(() => {
    if (!open || loading) return;
    document.getElementById(`${listboxId}-${highlight}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, loading, highlight, listboxId]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (loading && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      // The rows still held are the LAST search's: Enter now would download a PO nobody asked for.
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const po = results[highlight];
      if (po) void choose(po);
    }
  };

  const panel = open && pos && (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Export a vendor purchase order"
      className="fixed z-[9999] flex flex-col rounded-md border border-slate-200 bg-white shadow-lg"
      style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
    >
      <div className="shrink-0 space-y-2 border-b border-slate-100 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-800">Export a PO</span>
          <SegmentedControl<Format>
            aria-label="File type"
            value={format}
            onChange={setFormat}
            segments={[{ value: 'excel', label: 'Excel' }, { value: 'pdf', label: 'PDF' }]}
          />
        </div>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search PO number, vendor, vessel, client…"
          role="combobox"
          aria-expanded
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={!loading && results[highlight] ? `${listboxId}-${highlight}` : undefined}
          aria-label="Search purchase orders"
          className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-primary-500"
        />
        {downloadError && (
          <p role="alert" className="text-xs text-red-600">{downloadError}</p>
        )}
      </div>

      <div id={listboxId} role="listbox" aria-label="Purchase orders" className="min-h-0 flex-1 overflow-auto">
        {!query.trim() && !loading && results.length > 0 && (
          <div className="border-b border-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Most recent
          </div>
        )}
        {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}
        {!loading && failed && (
          <div className="px-3 py-3 text-xs text-slate-500">
            Purchase orders could not be loaded. Try again, or ask an administrator if it keeps happening.
          </div>
        )}
        {!loading && !failed && results.length === 0 && (
          <div className="px-3 py-3 text-xs text-slate-500">
            {query.trim()
              ? `No purchase order matches “${query.trim()}”. Try the PO number, the vendor or the vessel.`
              : 'No purchase orders yet.'}
          </div>
        )}
        {!loading && !failed && results.map((po, i) => (
          <button
            type="button"
            role="option"
            id={`${listboxId}-${i}`}
            aria-selected={i === highlight}
            key={po._id}
            disabled={downloadingId != null}
            onMouseEnter={() => setHighlight(i)}
            onClick={() => void choose(po)}
            className={`w-full border-b border-slate-100 px-3 py-2 text-left text-xs last:border-b-0 disabled:cursor-wait ${
              i === highlight ? 'bg-slate-50' : 'hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                {downloadingId === po._id && <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin motion-reduce:animate-none" />}
                {po.poNumber || '—'}
              </span>
              <StatusBadge status={po.status || 'draft'} />
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-2 text-slate-500">
              <span className="min-w-0 truncate">
                {po.vendorName || 'No vendor'}
                {po.vesselName ? ` • ${po.vesselName}` : ''}
                {po.createdAt ? ` • ${fmtDate(po.createdAt, '')}` : ''}
              </span>
              <span className="shrink-0 tabular-nums">{formatCurrency(po.totalPrice, 'TTD', { ceil: false })}</span>
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => { onExportList(); close(); }}
        disabled={listEmpty}
        className="shrink-0 border-t border-slate-100 px-3 py-2 text-left text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
      >
        Or download this list as a CSV
      </button>
    </div>
  );

  return (
    <>
      <Button
        ref={triggerRef}
        variant="primary"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
        icon={<Download aria-hidden="true" className="h-4 w-4" />}
      >
        Export
      </Button>
      {panel && createPortal(panel, document.body)}
    </>
  );
};

export default VendorPoExportPicker;
